import { useCallback, useEffect } from 'react';
import { SavedView, SavedViewModelType } from 'src/api/generated';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import ModalFooter from 'src/modules/shared/components/molecules/modals/modal-footer';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import { useHasRoles } from 'src/modules/shared/hooks/auth';
import {
  useCreateSavedView,
  useUpdateSavedView,
} from '../../../../hooks/api/saved-views';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Switch from 'src/modules/shared/components/atoms/switch';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Typography } from '@material-tailwind/react';
import { alertTextSizeClasses } from 'src/modules/shared/components/atoms/alert/alert.styles';

const savedViewSchema = z.object({
  // TODO: check when implementing i18n
  name: z.string().min(1, 'Debes ingresar un nombre'),
  description: z.string().optional(),
  isShared: z.boolean(),
});

type SavedViewFormData = z.infer<typeof savedViewSchema>;

interface SavedViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  model: SavedViewModelType;
  view?: SavedView;
  config: SavedView['config'];
  onSuccess?: (updatedView: SavedView) => void;
}

export const SavedViewModal: React.FC<SavedViewModalProps> = ({
  isOpen,
  onClose,
  model,
  view,
  config,
  onSuccess,
}) => {
  const { hasRole: isAdmin } = useHasRoles('Admin');
  const { user, loading: isLoadingUser } = useCurrentUser();
  const { createView, loading: isCreating } = useCreateSavedView(onSuccess);
  const { updateView, loading: isUpdating } = useUpdateSavedView(onSuccess);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<SavedViewFormData>({
    resolver: zodResolver(savedViewSchema),
    defaultValues: {
      name: '',
      description: '',
      isShared: false,
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    reset({
      name: view?.name || '',
      description: view?.description || '',
      isShared: view?.isShared || false,
    });
  }, [view, reset, isOpen]);

  const onSubmit = useCallback(
    async (data: SavedViewFormData) => {
      if (!user) return;

      const viewData = {
        ...data,
        model,
        config,
        isShared: isAdmin && data.isShared,
        userId: user.id,
      };

      try {
        let updatedView: SavedView;
        if (view) {
          updatedView = await updateView({ id: view.id, ...viewData });
        } else {
          updatedView = await createView(viewData);
        }
        if (onSuccess) {
          onSuccess(updatedView);
        }
        onClose();
      } catch (error) {
        console.error('Failed to save view:', error);
      }
    },
    [
      isAdmin,
      createView,
      onClose,
      updateView,
      config,
      model,
      user,
      view,
      onSuccess,
    ]
  );

  const isLoading = isCreating || isUpdating || isLoadingUser;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <h2 className="text-lg font-semibold">
          {/* TODO: check when implementing i18n */}
          {view ? 'Editar Vista' : 'Guardar Vista'}
        </h2>
      }
      body={
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 w-full py-2"
        >
          <Alert
            variant="info"
            icon={<InformationCircleIcon className="h-4 w-4" />}
            message={
              <Typography className={alertTextSizeClasses['sm']}>
                {/* TODO: check when implementing i18n */}
                Los filtros y ordenamientos actuales serán guardados en esta
                vista.
              </Typography>
            }
            className="mb-4"
          />

          <LabelTextInput
            // TODO: check when implementing i18n
            label="Nombre"
            inputProps={{
              error: errors.name?.message,
              ...register('name'),
            }}
          />
          <LabelTextArea
            // TODO: check when implementing i18n
            label="Descripción"
            inputProps={{
              error: errors.description?.message,
              // TODO: check when implementing i18n
              placeholder: 'Ingresa una descripción (opcional)',
              rows: 3,
              ...register('description'),
            }}
          />
          {isAdmin && (
            <div className="flex items-center justify-start gap-4">
              <label htmlFor="isShared" className="text-sm text-gray-900">
                {/* TODO: check when implementing i18n */}
                Compartir con todos los usuarios
              </label>
              <Controller
                name="isShared"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Switch
                    checked={value}
                    onChange={onChange}
                    size="sm"
                    variant="primary"
                  />
                )}
              />
            </div>
          )}
        </form>
      }
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleSubmit(onSubmit)}
          // TODO: check when implementing i18n
          confirmText={view ? 'Actualizar' : 'Guardar'}
          confirmDisabled={!user}
          loading={isLoading}
        />
      }
    />
  );
};
